import { Circle } from '@chakra-ui/react';

type UnreadBadgeProps = {
  /** Сколько непрочитано. 0 и меньше — значка нет вовсе. */
  count?: number;
  /**
   * Значок без числа. Нужен там, где счёт неизвестен или не важен: «что-то есть» — уже сигнал,
   * а выдуманная единица врала бы.
   */
  dot?: boolean;
  /** xs — поверх иконки в тесной навигации, sm — рядом с подписью, md — в строке списка. */
  size?: 'xs' | 'sm' | 'md';
  /**
   * Подложка, из которой вырезается ободок. Значок почти всегда лежит внахлёст на иконку, и без
   * ободка цифра сливается с ней. Цвет обязан совпасть с реальным фоном места — иначе кольцо
   * видно отдельным контуром.
   */
  ring?: string;
  /** Ярлык для тестов: у части старых копий он был, и терять его при переезде нельзя. */
  'data-testid'?: string;
};

const SIZE = { xs: '16px', sm: '18px', md: '20px' } as const;

/**
 * Непрочитанное одним значком.
 *
 * Заведён 06.09.2026, потому что этот кружок к тому моменту был нарисован руками шесть раз — в
 * шапке, в сайдбаре, в нижней навигации, в меню пользователя и дважды в новой работе по чату
 * сессии, — и копии разошлись по оттенку: часть на `purple.400`, часть на `purple.solid`. На
 * десктопе «Чаты 2» в сайдбаре и «Все 2» во вкладке рядом были разного тона.
 *
 * Цвет один и это `purple.solid`: заливка, а не плоский акцент, — та самая ступень, которая
 * проходит по контрасту с белой цифрой.
 */
export const UnreadBadge = ({
  count = 0,
  dot = false,
  size = 'sm',
  ring,
  'data-testid': testId,
}: UnreadBadgeProps) => {
  if (!dot && count <= 0) {
    return null;
  }

  /*
    Ободок — контуром, а не рамкой: рамка входит в те же 16 пикселей, отнимает у цифры четыре из
    них с каждой стороны, и в нижней навигации круг растягивался в овал 18×16. Контур рисуется
    снаружи коробки, места не занимает и повторяет скругление.
  */
  const ringProps = ring
    ? { outlineWidth: '2px', outlineStyle: 'solid', outlineColor: ring }
    : {};

  if (dot) {
    return <Circle size="10px" bg="purple.solid" flexShrink={0} data-testid={testId} {...ringProps} />;
  }

  return (
    <Circle
      /*
        Не фиксированный размер, а минимальный: круг с жёсткой шириной обрезал двузначное
        число — «12» превращалось в «1». Ширина растёт от содержимого, высота остаётся, и на
        одной цифре это по-прежнему ровный круг.
      */
      minW={SIZE[size]}
      h={SIZE[size]}
      px={1}
      bg="purple.solid"
      color="white"
      textStyle="micro"
      flexShrink={0}
      data-testid={testId}
      {...ringProps}
    >
      {/* Больше сотни человек не считает, а три цифры ломают круг. */}
      {Math.min(count, 99)}
    </Circle>
  );
};
